import React, { useEffect, useState } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import * as A from 'pages/components/AuthShell/AuthShell.styled';
import * as S from './SignInModal.styled';
import Loader from 'pages/components/Loader/Loader';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useCreds } from 'redux/global';
import { isValidatedEmail, getApiUrl } from 'utils';
import {
  selectIsStaySignedIn,
  selectPassword,
  selectUserEmail,
  setIsStaySignedIn as setSavedIsStaySignedIn,
  setUserEmail,
  setPassword as setUserPassword,
} from 'pages/app/redux';
import { toast } from 'utils/toast';
import { useHistory } from 'react-router-dom';
import { setIsStaff } from 'pages/SignIn/redux';
import { selectOrganizationData, setOrganizationData } from 'pages/redux';
import { getOrganization, organizationFromGetOrganizationData } from 'pages/Navigation/redux';
import { usePath } from 'pages/hooks';
import { ensureHtmlBootSplash, markLogoSplashForSignIn } from 'pages/components/Loader/BootSplash';

export type OrganizationsData = {
  name: string,
  id: string,
}

const SignInModal = ({
  onExit,
  setIsLoading,
  variant = 'modal',
}:{
  onExit: Function,
  setIsLoading?: Function,
  variant?: 'page' | 'modal',
}) => {
  const savedIsStaySignedIn = useAppSelector(selectIsStaySignedIn);
  const savedEmail = useAppSelector(selectUserEmail);
  const savedPassword = useAppSelector(selectPassword);
  const [isStaySignedIn] = useState<boolean>(savedIsStaySignedIn);
  const [email, setEmail] = useState<string>(isStaySignedIn ? savedEmail : '');
  const [password, setPassword] = useState<string>(isStaySignedIn ? savedPassword : '');
  const [passType, setPassType] = useState<string>('password');
  const [loading, setLoading1] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>();
  const [step, setStep] = useState<number>(1);
  const [organizationsData, setOrganizationsData] = useState<OrganizationsData[]>();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>();
  const [isSelectOrganizationError, setIsSelectOrganizationError] = useState<boolean>(false);
  const organizationData = useAppSelector(selectOrganizationData);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { login } = useCreds();
  const { navigateToHome } = usePath();

  const setLoading = (bool: boolean) => {
    setIsLoading?.(bool);
    setLoading1(bool);
  };

  const finishWithOrganization = async (organizationId: string) => {
    // Skip getOrganization when this org is already loaded in the session.
    if (String(organizationData?.id || '') === String(organizationId) && organizationData) {
      dispatch(setSavedIsStaySignedIn(isStaySignedIn));
      dispatch(setUserEmail(email.toLowerCase()));
      if (isStaySignedIn) dispatch(setUserPassword(password));
      else dispatch(setUserPassword(''));
      dispatch(setIsStaff(true));
      toast('Succesfully logged in', { type: 'success' });
      onExit();
      navigateToHome();
      return;
    }

    const res = await dispatch(getOrganization(String(organizationId), true));
    if (res.status !== 'success') {
      toast(res.data, { type: 'error' });
      return;
    }
    markLogoSplashForSignIn();
    ensureHtmlBootSplash();
    const organization = organizationFromGetOrganizationData(res.data);
    if (!organization) {
      toast('Could not load organization', { type: 'error' });
      return;
    }
    dispatch(setOrganizationData(organization));
    dispatch(setSavedIsStaySignedIn(isStaySignedIn));
    dispatch(setUserEmail(email.toLowerCase()));
    if (isStaySignedIn) dispatch(setUserPassword(password));
    else dispatch(setUserPassword(''));
    dispatch(setIsStaff(true));
    toast('Succesfully logged in', { type: 'success' });
    onExit();
    navigateToHome();
  };

  /** One auth call; org picker only when auth says the seat spans multiple orgs. */
  const finalLogIn = async (sId?: string) => {
    if (!isValidatedEmail(email)) {
      setEmailError('Email is invalid!');
      return;
    }
    if (loading) return;
    setLoading(true);

    const result = await dispatch(login({
      email: email.toLowerCase(),
      password,
      organizationId: sId || selectedOrganizationId,
    }));

    if (result.kind === 'pick') {
      setOrganizationsData(result.organizations);
      setStep(2);
      setLoading(false);
      return;
    }
    if (result.kind !== 'ok') {
      setLoading(false);
      return;
    }

    await finishWithOrganization(result.organizationId);
    setLoading(false);
  };

  const onClickLogIn0 = async () => {
    // No getLogInBranches — auth handles single-org login and multi-org pick.
    await finalLogIn();
  };

  useEffect(() => {
    // Wake Cloud Run before Log in — cold instances often fail OPTIONS with no ACAO (looks like CORS).
    void fetch(getApiUrl(), { method: 'GET', mode: 'cors', cache: 'no-store' }).catch(() => {});
  }, []);

  useEffect(() => {
    // Other-tab / restore paths may set org without going through finalLogIn.
    if (Object.keys(organizationData || {}).length && !loading) {
      navigateToHome();
      onExit();
    }
  }, [organizationData]);

  const Step1 = () => (
    <>
      <A.Field>
        Email
        <A.Input
          type="email"
          autoComplete="email"
          value={email}
          $error={Boolean(emailError)}
          placeholder="you@organization.com"
          onChange={(e) => {
            setEmail(e.target.value.toLowerCase());
            if (e.target.value.toLowerCase() === '' || isValidatedEmail(e.target.value.toLowerCase())) {
              setEmailError('');
            } else {
              setEmailError('Email is invalid!');
            }
          }}
        />
        {emailError ? <A.Error>{emailError}</A.Error> : null}
      </A.Field>

      <A.Field>
        Password
        <A.PasswordWrap>
          <A.Input
            type={passType === 'password' ? 'password' : 'text'}
            autoComplete="current-password"
            value={password}
            placeholder="Your password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <A.EyeBtn
            type="button"
            aria-label="toggle password visibility"
            onClick={() => setPassType(passType === 'password' ? 'text' : 'password')}
          >
            {passType === 'password' ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
          </A.EyeBtn>
        </A.PasswordWrap>
      </A.Field>

      <A.LinkRow>
        <A.TextLink
          type="button"
          onClick={() => {
            history.push('/forgot');
            onExit();
          }}
        >
          Forgot password
        </A.TextLink>
      </A.LinkRow>

      <A.PrimaryBtn type="button" onClick={() => onClickLogIn0()}>
        Log in
      </A.PrimaryBtn>
    </>
  );

  const onClickStart = () => {
    setIsSelectOrganizationError(false);
    if (!selectedOrganizationId) {
      setIsSelectOrganizationError(true);
      toast('Please select branch', { type: 'warning' });
      return;
    }
    finalLogIn();
  };

  const Step2 = () => (
    <>
      <A.Hint>Select the organization branch you want to open.</A.Hint>
      <A.Field>
        Organization branch
        <A.Select
          $error={isSelectOrganizationError}
          defaultValue=""
          onChange={(e) => {
            setIsSelectOrganizationError(false);
            const thisOrganizationData = organizationsData?.find((d) => d.name === e.target.value);
            if (thisOrganizationData) {
              setSelectedOrganizationId(thisOrganizationData.id);
            }
          }}
        >
          <option value="" disabled>Select organization branch</option>
          {organizationsData?.map((d) => (
            <option key={`hData_${d.name}_${d.id}`} value={d.name} selected={selectedOrganizationId === d.id}>
              {d.name}
            </option>
          ))}
        </A.Select>
      </A.Field>
      <A.BtnRow>
        <A.GhostBtn
          type="button"
          onClick={() => {
            setSelectedOrganizationId('');
            setIsSelectOrganizationError(false);
            setStep(1);
          }}
        >
          Back
        </A.GhostBtn>
        <A.PrimaryBtn type="button" onClick={() => onClickStart()}>
          Continue
        </A.PrimaryBtn>
      </A.BtnRow>
    </>
  );

  const body = (
    <>
      <A.FormTitle>{step === 1 ? 'Sign in' : 'Choose branch'}</A.FormTitle>
      <A.FormSub>
        {step === 1
          ? 'Use your staff email and password to access Tripple.'
          : 'Your account is linked to more than one property.'}
      </A.FormSub>
      {step === 1 ? Step1() : Step2()}
    </>
  );

  if (loading) {
    return <Loader isSticky message={step === 1 ? 'Signing in…' : 'Opening branch…'} />;
  }

  if (variant === 'page') {
    return <S.PageForm>{body}</S.PageForm>;
  }

  return (
    <S.ModalCard>
      {body}
    </S.ModalCard>
  );
};

export default SignInModal;
